import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { Languages, Sparkles, Save } from 'lucide-react';
import { toast } from '../../../../../../ui/toast';
import {
  deleteTranslation,
  getJobPost,
  saveTranslation,
  suggestTranslation,
} from '../../../../../../utils/api_service/JobPosting';

const SUPPORTED_LANGUAGES = [
  { value: 'id', label: 'Indonesian (Bahasa)' },
  { value: 'ms', label: 'Malay (Bahasa Melayu)' },
  { value: 'zh', label: 'Chinese (Simplified)' },
  { value: 'zh-TW', label: 'Chinese (Traditional)' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'th', label: 'Thai' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'ar', label: 'Arabic' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'si', label: 'Sinhala' },
  { value: 'ta', label: 'Tamil' },
  { value: 'ru', label: 'Russian' },
];

export interface TranslationPanelProps {
  jobPostingId: string;
  availableLanguages: string[];
  currentJsonTemplate: string;
  currentHtmlTemplate: string;
  onLoadTranslation: (
    jsonTemplate: string,
    htmlTemplate: string,
    lang: string,
    autoSave?: boolean,
  ) => void;
  onTranslationsChanged: (langs: string[]) => void;
  onBeforeSuggest?: () => Promise<boolean>;
  onGetCurrentContent?: () => { html: string; json: string };
}

export const TranslationPanel: React.FC<TranslationPanelProps> = ({
  jobPostingId,
  availableLanguages,
  currentJsonTemplate,
  currentHtmlTemplate,
  onLoadTranslation,
  onTranslationsChanged,
  onBeforeSuggest,
  onGetCurrentContent,
}) => {
  const [selectedLang, setSelectedLang] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeLang, setActiveLang] = useState<string | null>(null);
  const [deletingLang, setDeletingLang] = useState<string | null>(null);
  const [loadingLang, setLoadingLang] = useState<string | null>(null);

  const langLabel = (code: string) =>
    SUPPORTED_LANGUAGES.find((l) => l.value === code)?.label ?? code;

  const saveCurrentState = async (): Promise<boolean> => {
    if (!activeLang) {
      if (onBeforeSuggest) {
        return await onBeforeSuggest();
      }
      return true;
    } else {
      if (onGetCurrentContent) {
        try {
          const content = onGetCurrentContent();
          const res = await saveTranslation(jobPostingId, activeLang, {
            jsonTemplate: content.json,
            htmlTemplate: content.html,
          });
          return res.ok;
        } catch {
          return false;
        }
      }
      return true;
    }
  };

  const handleLoadExisting = async (lang: string) => {
    if (activeLang === lang) return;
    setLoadingLang(lang);
    try {
      await saveCurrentState();
      const res = await getJobPost(jobPostingId, lang);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const jobPost = data.jobPosting || data;
      onLoadTranslation(
        jobPost.jsonTemplate || '',
        jobPost.htmlTemplate || '',
        lang,
      );
      setActiveLang(lang);
    } catch {
      toast.error(`Failed to load translation for ${langLabel(lang)}`, {
        position: 'bottom-center',
      });
    } finally {
      setLoadingLang(null);
    }
  };

  const handleLoadOriginal = async () => {
    if (activeLang === null) return;
    setLoadingLang('original');
    try {
      await saveCurrentState();
      const res = await getJobPost(jobPostingId, 'en');
      if (!res.ok) throw new Error();
      const data = await res.json();
      const jobPost = data.jobPosting || data;
      onLoadTranslation(
        jobPost.jsonTemplate || '',
        jobPost.htmlTemplate || '',
        '',
      );
      setActiveLang(null);
    } catch {
      toast.error('Failed to load original template', {
        position: 'bottom-center',
      });
    } finally {
      setLoadingLang(null);
    }
  };

  const handleSuggest = async () => {
    if (!selectedLang) {
      toast.error('Select a target language first', {
        position: 'bottom-center',
      });
      return;
    }
    if (!jobPostingId) {
      toast.error('Save the job posting settings first', {
        position: 'bottom-center',
      });
      return;
    }
    setSuggesting(true);
    try {
      if (onBeforeSuggest) {
        const saved = await onBeforeSuggest();
        if (!saved) {
          toast.error(
            'Failed to save current template before generating suggestion',
            { position: 'bottom-center' },
          );
          setSuggesting(false);
          return;
        }
      }
      const res = await suggestTranslation(jobPostingId, selectedLang);
      if (!res.ok) throw new Error();
      const data = await res.json();
      onLoadTranslation(
        data.jsonTemplate,
        data.htmlTemplate,
        selectedLang,
        true,
      );
      setActiveLang(selectedLang);
      setSelectedLang('');
      toast.success(
        `Translation suggested for ${langLabel(selectedLang)}. Review and save.`,
        {
          position: 'bottom-center',
        },
      );
    } catch {
      toast.error('Failed to generate translation suggestion', {
        position: 'bottom-center',
      });
    } finally {
      setSuggesting(false);
    }
  };

  const handleSave = async () => {
    if (!activeLang) {
      toast.error('Load a translation first', { position: 'bottom-center' });
      return;
    }
    setSaving(true);
    try {
      let jsonTemplate = currentJsonTemplate;
      let htmlTemplate = currentHtmlTemplate;
      if (onGetCurrentContent) {
        const content = onGetCurrentContent();
        jsonTemplate = content.json;
        htmlTemplate = content.html;
      }
      const res = await saveTranslation(jobPostingId, activeLang, {
        jsonTemplate,
        htmlTemplate,
      });
      if (!res.ok) throw new Error();
      const updated = availableLanguages.includes(activeLang)
        ? availableLanguages
        : [...availableLanguages, activeLang];
      onTranslationsChanged(updated);
      setSelectedLang('');
      toast.success(`Translation saved for ${langLabel(activeLang)}`, {
        position: 'bottom-center',
      });
    } catch {
      toast.error('Failed to save translation', { position: 'bottom-center' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lang: string) => {
    setDeletingLang(lang);
    try {
      const res = await deleteTranslation(jobPostingId, lang);
      if (!res.ok) throw new Error();
      const updated = availableLanguages.filter((l) => l !== lang);
      onTranslationsChanged(updated);
      if (activeLang === lang) setActiveLang(null);
      toast.success(`Translation removed for ${langLabel(lang)}`, {
        position: 'bottom-center',
      });
    } catch {
      toast.error('Failed to delete translation', {
        position: 'bottom-center',
      });
    } finally {
      setDeletingLang(null);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: 2,
        boxShadow: (theme) => theme.shadows[1],
      }}
    >
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Languages size={18} color='#059669' />
        <Typography variant='subtitle2' sx={{ fontWeight: 800, color: 'text.primary' }}>
          Translations
        </Typography>
      </Box>

      {/* Existing translations */}
      {(availableLanguages.length > 0 || activeLang) && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label='Original (English)'
            onClick={handleLoadOriginal}
            disabled={loadingLang !== null || suggesting || saving}
            icon={loadingLang === 'original' ? <CircularProgress size={14} color='inherit' /> : undefined}
            variant={activeLang === null ? 'filled' : 'outlined'}
            color={activeLang === null ? 'primary' : 'default'}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              cursor: 'pointer',
            }}
          />

          {availableLanguages.map((lang) => (
            <Chip
              key={lang}
              label={langLabel(lang)}
              onClick={() => handleLoadExisting(lang)}
              onDelete={() => handleDelete(lang)}
              disabled={deletingLang === lang}
              icon={loadingLang === lang ? <CircularProgress size={14} color='inherit' /> : undefined}
              variant={activeLang === lang ? 'filled' : 'outlined'}
              color={activeLang === lang ? 'primary' : 'default'}
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                cursor: 'pointer',
              }}
            />
          ))}
        </Box>
      )}

      {/* Suggest new translation */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: { sm: 'center' } }}>
        <FormControl size='small' sx={{ flex: 1, minWidth: 200 }}>
          <Select
            value={selectedLang}
            onChange={(e) => setSelectedLang(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value=''>
              <em>Select language…</em>
            </MenuItem>
            {SUPPORTED_LANGUAGES.filter(
              (l) => !availableLanguages.includes(l.value),
            ).map((l) => (
              <MenuItem key={l.value} value={l.value}>
                {l.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          onClick={handleSuggest}
          disabled={suggesting || !selectedLang || !jobPostingId}
          variant='contained'
          color='primary'
          startIcon={
            suggesting ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <Sparkles size={16} />
            )
          }
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 2,
            whiteSpace: 'nowrap',
          }}
        >
          {suggesting ? 'Translating…' : 'AI Translate'}
        </Button>

        {activeLang && (
          <Button
            onClick={handleSave}
            disabled={saving}
            variant='outlined'
            color='primary'
            startIcon={
              saving ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <Save size={16} />
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              whiteSpace: 'nowrap',
            }}
          >
            {saving ? 'Saving…' : `Save ${langLabel(activeLang)}`}
          </Button>
        )}
      </Box>

      {!jobPostingId && (
        <Typography variant='caption' sx={{ mt: 1.5, display: 'block', color: 'warning.main', fontWeight: 500 }}>
          Complete creating the job post and save it (including a valid URL).
          Once the job post is saved, you can add or edit language translations
          for this job post.
        </Typography>
      )}
    </Paper>
  );
};

export default TranslationPanel;
